import {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useMetaTags} from '../../services/useMetaTags';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';
import OrganizationListItem from './ListItem';
import trackPageView from '../../services/trackPageView';
import '../../assets/styles/organizations/organizationBrowser.scss';

const PER_PAGE = 12;
const EMPTY_DATA = {
    items: [],
    pagination: {
        page: 1,
        perPage: PER_PAGE,
        totalItems: 0,
        totalPages: 0,
        hasMore: false,
    },
    filtersMeta: {
        types: [],
        letters: [],
        cities: [],
        contactOptions: [],
    },
    activeFilters: {
        search: '',
        type: 0,
        letter: '',
        city: '',
        hasPhone: false,
        hasEmail: false,
        hasWebsite: false,
        hasAddress: false,
        page: 1,
    },
};

function parseBooleanParam(value) {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value > 0;
    }

    if (typeof value !== 'string') {
        return false;
    }

    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
}

function parsePositiveInt(value, fallback = 1) {
    const parsed = Number.parseInt(String(value || ''), 10);

    if (!Number.isFinite(parsed) || parsed < 1) {
        return fallback;
    }

    return parsed;
}

function parseLetter(value) {
    const normalized = String(value || '').trim();

    if (normalized === '') {
        return '';
    }

    return normalized.charAt(0).toUpperCase();
}

function parseQueryState(search) {
    const params = new URLSearchParams(search || '');

    return {
        search: params.get('search') || '',
        type: Math.max(0, parsePositiveInt(params.get('type') || '0', 0)),
        letter: parseLetter(params.get('letter') || ''),
        city: params.get('city') || '',
        hasPhone: parseBooleanParam(params.get('hasPhone')),
        hasEmail: parseBooleanParam(params.get('hasEmail')),
        hasWebsite: parseBooleanParam(params.get('hasWebsite')),
        hasAddress: parseBooleanParam(params.get('hasAddress')),
        page: parsePositiveInt(params.get('page') || '1', 1),
    };
}

function buildBrowserQuery(state) {
    const params = new URLSearchParams();

    if ((state.search || '').trim() !== '') {
        params.set('search', state.search.trim());
    }

    if ((state.type || 0) > 0) {
        params.set('type', String(state.type));
    }

    if ((state.letter || '').trim() !== '') {
        params.set('letter', state.letter.trim());
    }

    if ((state.city || '').trim() !== '') {
        params.set('city', state.city.trim());
    }

    if (state.hasPhone) {
        params.set('hasPhone', '1');
    }

    if (state.hasEmail) {
        params.set('hasEmail', '1');
    }

    if (state.hasWebsite) {
        params.set('hasWebsite', '1');
    }

    if (state.hasAddress) {
        params.set('hasAddress', '1');
    }

    if ((state.page || 1) > 1) {
        params.set('page', String(state.page));
    }

    return params.toString();
}

function buildApiQuery(state) {
    const params = new URLSearchParams();
    params.set('page', String(state.page || 1));
    params.set('perPage', String(PER_PAGE));

    if ((state.search || '').trim() !== '') {
        params.set('search', state.search.trim());
    }

    if ((state.type || 0) > 0) {
        params.set('type', String(state.type));
    }

    if ((state.letter || '').trim() !== '') {
        params.set('letter', state.letter.trim());
    }

    if ((state.city || '').trim() !== '') {
        params.set('city', state.city.trim());
    }

    if (state.hasPhone) {
        params.set('hasPhone', '1');
    }

    if (state.hasEmail) {
        params.set('hasEmail', '1');
    }

    if (state.hasWebsite) {
        params.set('hasWebsite', '1');
    }

    if (state.hasAddress) {
        params.set('hasAddress', '1');
    }

    return params.toString();
}

function getPaginationItems(currentPage, totalPages) {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    const items = [1];
    const from = Math.max(2, currentPage - 1);
    const to = Math.min(totalPages - 1, currentPage + 1);

    if (from > 2) {
        items.push('ellipsis-left');
    }

    for (let page = from; page <= to; page += 1) {
        items.push(page);
    }

    if (to < totalPages - 1) {
        items.push('ellipsis-right');
    }

    items.push(totalPages);

    return items;
}

function hasActiveFilters(state) {
    return (state.search || '').trim() !== ''
        || (state.type || 0) > 0
        || (state.letter || '').trim() !== ''
        || (state.city || '').trim() !== ''
        || !!state.hasPhone
        || !!state.hasEmail
        || !!state.hasWebsite
        || !!state.hasAddress;
}

function booleanSelectValue(value) {
    return value ? '1' : '0';
}

export default function OrganizationBrowser() {
    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const queryState = useMemo(() => parseQueryState(location.search), [location.search]);
    const [data, setData] = useState(EMPTY_DATA);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchValue, setSearchValue] = useState(queryState.search);
    const [draftFilters, setDraftFilters] = useState({
        city: queryState.city,
        hasPhone: queryState.hasPhone,
        hasEmail: queryState.hasEmail,
        hasWebsite: queryState.hasWebsite,
        hasAddress: queryState.hasAddress,
    });

    useMetaTags({
        title: t('organizations.title'),
        description: t('app.description'),
        metas: [
            {name: 'keywords', content: t('app.keywords')},
        ],
    });

    useEffect(() => {
        trackPageView(t('organizations.title'));
    }, [t]);

    useEffect(() => {
        setSearchValue(queryState.search);
        setDraftFilters({
            city: queryState.city,
            hasPhone: queryState.hasPhone,
            hasEmail: queryState.hasEmail,
            hasWebsite: queryState.hasWebsite,
            hasAddress: queryState.hasAddress,
        });
    }, [queryState]);

    useEffect(() => {
        let cancelled = false;
        const apiQuery = buildApiQuery(queryState);

        setLoading(true);

        fetch(`/api/v1/organizations/browser?${apiQuery}`)
            .then(async (res) => {
                const result = await res.json().catch(() => null);
                const {kind, redirectUrl} = parseRouteResponse(res, result);

                if (cancelled) {
                    return null;
                }

                if (kind === 'not_found') {
                    setNotFound(true);
                    setLoading(false);
                    return null;
                }

                if ((kind === 'unauthorized' || kind === 'redirect' || kind === 'error') && redirectUrl) {
                    window.location.href = redirectUrl;
                    return null;
                }

                if (kind === 'error') {
                    setLoading(false);
                    return null;
                }

                setNotFound(false);
                return result;
            })
            .then((result) => {
                if (cancelled || !result) {
                    return;
                }

                setData({
                    ...EMPTY_DATA,
                    ...result,
                    pagination: {
                        ...EMPTY_DATA.pagination,
                        ...(result.pagination || {}),
                    },
                    filtersMeta: {
                        ...EMPTY_DATA.filtersMeta,
                        ...(result.filtersMeta || {}),
                    },
                    activeFilters: {
                        ...EMPTY_DATA.activeFilters,
                        ...(result.activeFilters || {}),
                    },
                });
                setLoading(false);
            })
            .catch(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [queryState]);

    const applyRouteState = useCallback((nextState, {replace = false} = {}) => {
        const nextQuery = buildBrowserQuery(nextState);
        const nextLocation = {
            pathname: '/organizations',
            search: nextQuery ? `?${nextQuery}` : '',
        };

        if (replace) {
            history.replace(nextLocation);
            return;
        }

        history.push(nextLocation);
    }, [history]);

    const activePage = data.activeFilters?.page || queryState.page || 1;

    const handleSearchSubmit = useCallback((event) => {
        event.preventDefault();

        applyRouteState({
            ...queryState,
            search: searchValue,
            page: 1,
        });
    }, [applyRouteState, queryState, searchValue]);

    const handleTypeChange = useCallback((event) => {
        applyRouteState({
            ...queryState,
            type: Math.max(0, parsePositiveInt(event.target.value || '0', 0)),
            letter: '',
            page: 1,
        });
    }, [applyRouteState, queryState]);

    const handleLetterClick = useCallback((letter) => {
        applyRouteState({
            ...queryState,
            letter,
            page: 1,
        });
    }, [applyRouteState, queryState]);

    const handlePageClick = useCallback((page) => {
        if (page < 1 || page === activePage) {
            return;
        }

        applyRouteState({
            ...queryState,
            page,
        });

        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, [activePage, applyRouteState, queryState]);

    const handleApplyFilters = useCallback(() => {
        applyRouteState({
            ...queryState,
            city: draftFilters.city,
            hasPhone: draftFilters.hasPhone,
            hasEmail: draftFilters.hasEmail,
            hasWebsite: draftFilters.hasWebsite,
            hasAddress: draftFilters.hasAddress,
            page: 1,
        });
        setDrawerOpen(false);
    }, [applyRouteState, draftFilters, queryState]);

    const handleResetFilters = useCallback(() => {
        const clearedFilters = {
            city: '',
            hasPhone: false,
            hasEmail: false,
            hasWebsite: false,
            hasAddress: false,
        };

        setDraftFilters(clearedFilters);
        applyRouteState({
            ...queryState,
            ...clearedFilters,
            page: 1,
        });
    }, [applyRouteState, queryState]);

    const typeOptions = data.filtersMeta.types || [];
    const letterOptions = data.filtersMeta.letters || [];
    const cityOptions = data.filtersMeta.cities || [];
    const contactOptions = data.filtersMeta.contactOptions?.length
        ? data.filtersMeta.contactOptions
        : [
            {value: 'hasPhone'},
            {value: 'hasEmail'},
            {value: 'hasWebsite'},
            {value: 'hasAddress'},
        ];
    const paginationItems = getPaginationItems(activePage, data.pagination.totalPages || 0);
    const showEmptyState = !loading && (data.items || []).length === 0;

    if (notFound) {
        return <NotFoundPage />;
    }

    return (
        <section className="org-browser">
            <div className="container">
                <AdvertiseBlock unitId="organizations1" blockLocation="top" />

                <div className="org-top">
                    <div className="org-top__title">
                        <h1>{t('organizations.title')}</h1>
                        <p>{t('organizations.browser.subtitle')}</p>
                    </div>

                    <div className="org-top__controls">
                        <form className="org-top__search" onSubmit={handleSearchSubmit} role="search">
                            <div className="org-top__search-field">
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M10.5 3a7.5 7.5 0 105.02 13.08l3.2 3.2a1 1 0 001.41-1.41l-3.2-3.2A7.5 7.5 0 0010.5 3zm0 2a5.5 5.5 0 110 11 5.5 5.5 0 010-11z" fill="currentColor" />
                                </svg>
                                <input
                                    type="search"
                                    value={searchValue}
                                    onChange={(event) => setSearchValue(event.target.value)}
                                    placeholder={t('organizations.browser.searchPlaceholder')}
                                />
                            </div>
                            <button type="submit" className="org-btn org-btn--primary">
                                {t('search.submit')}
                            </button>
                        </form>

                        <label className="org-top__select">
                            <span>{t('organizations.filterBy')}</span>
                            <select value={queryState.type} onChange={handleTypeChange}>
                                <option value="0">{t('organizations.all')}</option>
                                {typeOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <button type="button" className="org-btn org-btn--ghost" onClick={() => setDrawerOpen(true)}>
                            {t('organizations.browser.advanced')}
                        </button>

                        <div className="org-top__meta">
                            <span>{t('organizations.browser.found')}</span>
                            <strong>{data.pagination.totalItems || 0}</strong>
                        </div>
                    </div>
                </div>

                <nav className="org-alpha" aria-label={t('organizations.browser.alphabetLabel')}>
                    <button
                        type="button"
                        className={`org-alpha__chip ${(queryState.letter || '') === '' ? 'is-active' : ''}`}
                        onClick={() => handleLetterClick('')}
                    >
                        {t('organizations.all')}
                    </button>

                    {letterOptions.map((letter) => (
                        <button
                            key={letter}
                            type="button"
                            className={`org-alpha__chip ${queryState.letter === letter ? 'is-active' : ''}`}
                            onClick={() => handleLetterClick(letter)}
                        >
                            {letter}
                        </button>
                    ))}
                </nav>

                {loading && (
                    <div className="org-browser__status">
                        {t('organizations.browser.loading')}
                    </div>
                )}

                {showEmptyState && (
                    <div className="org-browser__empty">
                        {t(hasActiveFilters(queryState) ? 'organizations.browser.noResultsFor' : 'organizations.browser.noResults')}
                    </div>
                )}

                {!loading && (data.items || []).length > 0 && (
                    <div className="org-grid">
                        {(data.items || []).map((item) => (
                            <OrganizationListItem key={item.id} item={item} t={t} />
                        ))}
                    </div>
                )}

                {(data.pagination.totalPages || 0) > 1 && (
                    <nav className="org-pagination" aria-label={t('organizations.browser.paginationLabel')}>
                        <button
                            type="button"
                            className="org-pagination__button"
                            onClick={() => handlePageClick(activePage - 1)}
                            disabled={activePage <= 1}
                        >
                            {t('search.prev')}
                        </button>

                        {paginationItems.map((item) => {
                            if (typeof item !== 'number') {
                                return (
                                    <span key={item} className="org-pagination__ellipsis">
                                        ...
                                    </span>
                                );
                            }

                            return (
                                <button
                                    key={item}
                                    type="button"
                                    className={`org-pagination__button ${item === activePage ? 'is-active' : ''}`}
                                    onClick={() => handlePageClick(item)}
                                >
                                    {item}
                                </button>
                            );
                        })}

                        <button
                            type="button"
                            className="org-pagination__button"
                            onClick={() => handlePageClick(activePage + 1)}
                            disabled={activePage >= (data.pagination.totalPages || 1)}
                        >
                            {t('search.next')}
                        </button>
                    </nav>
                )}
            </div>

            <div
                className={`org-browser__backdrop ${drawerOpen ? 'is-open' : ''}`}
                onClick={() => setDrawerOpen(false)}
                aria-hidden={!drawerOpen}
            />

            <aside
                className={`org-drawer ${drawerOpen ? 'is-open' : ''}`}
                hidden={!drawerOpen}
                aria-label={t('organizations.browser.drawerTitle')}
                aria-hidden={!drawerOpen}>
                <div className="org-drawer__head">
                    <strong>{t('organizations.browser.drawerTitle')}</strong>
                    <button
                        type="button"
                        className="org-drawer__close"
                        onClick={() => setDrawerOpen(false)}
                        aria-label={t('organizations.browser.close')}>
                        <span>✕</span>
                    </button>
                </div>

                <div className="org-drawer__body">
                    <div className="org-drawer__group">
                        <h2>{t('organizations.browser.filters.city')}</h2>
                        <select
                            className="org-drawer__select"
                            value={draftFilters.city}
                            onChange={(event) => setDraftFilters((current) => ({
                                ...current,
                                city: event.target.value,
                            }))}>
                            <option value="">{t('organizations.all')}</option>
                            {cityOptions.map((city) => (
                                <option key={city} value={city}>
                                    {city}
                                </option>
                            ))}
                        </select>
                    </div>

                    {contactOptions.map((option) => (
                        <div key={option.value} className="org-drawer__group">
                            <h2>{t(`organizations.browser.filters.${option.value}`)}</h2>
                            <select
                                className="org-drawer__select"
                                value={booleanSelectValue(draftFilters[option.value])}
                                onChange={(event) => setDraftFilters((current) => ({
                                    ...current,
                                    [option.value]: event.target.value === '1',
                                }))}>
                                <option value="0">{t('organizations.all')}</option>
                                <option value="1">{t('organizations.browser.filters.onlyWith')}</option>
                            </select>
                        </div>
                    ))}
                </div>

                <div className="org-drawer__actions">
                    <button type="button" className="org-btn org-btn--primary" onClick={handleApplyFilters}>
                        {t('search.filters.apply')}
                    </button>
                    <button type="button" className="org-btn org-btn--ghost" onClick={handleResetFilters}>
                        {t('search.filters.reset')}
                    </button>
                </div>
            </aside>
        </section>
    );
}
